/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `PhoneOutlineProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface PhoneOutlineProps {
    /**
     * A string representing the CSS class to be applied to the PhoneOutlineIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#282828'
     */
    color1?: string;
    /**
     * A number or string representing the height of the PhoneOutlineIcon element.
     *
     * @default 22
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'PhoneOutline'
     */
    testId?: string;
    /**
     * A number or string representing the height of the PhoneOutlineIcon element.
     *
     * @default 22
     */
    width?: number | string;
}

/**
 * The `PhoneOutline` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `PhoneOutlineProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the PhoneOutlineIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the PhoneOutlineIcon element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the PhoneOutlineIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const PhoneOutlineComponent = <PhoneOutline className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const PhoneOutline = ({
    className = '',
    color1 = '#282828',
    height = 22,
    ref,
    testId = 'PhoneOutline',
    width = 22
}: PhoneOutlineProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 22 22"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M20.89 15.92v3a2 2 0 0 1-2.18 2 20 20 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A20 20 0 0 1 1 3.18 2 2 0 0 1 3 1h3a2 2 0 0 1 2 1.72q.18 1.44.7 2.81a2 2 0 0 1-.45 2.11L6.98 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.1-.45q1.38.5 2.82.7a2 2 0 0 1 1.72 2.03" fill="transparent" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
);

PhoneOutline.displayName = 'PhoneOutline';